/**
 * Chat over the whole workspace. The transcript is sent to the server, which
 * attaches the client book and pipeline before calling the model.
 */
import { useState, useRef, useEffect } from 'react';
import { useApp } from '../store/AppContext.jsx';
import { COPILOT_PROMPTS } from '../constants/defaults.js';
import Icon from '../lib/icons.jsx';
import Markdown from '../lib/markdown.jsx';
import { Card, CardHead, AIBadge, Spinner, Avatar } from '../components/Primitives.jsx';

export default function Copilot() {
  const { S, chat, chatBusy, sendChat, setChat } = useApp();
  const [text, setText] = useState('');
  const messagesRef = useRef(null);
  const inputRef = useRef(null);

  /* Keep the newest message in view as the conversation grows. */
  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat, chatBusy]);

  useEffect(() => { if (!chatBusy) inputRef.current?.focus(); }, [chatBusy]);

  const submit = value => {
    const t = (value ?? text).trim();
    if (!t) return;
    setText('');
    sendChat(t);
  };

  return (
    <Card>
      <CardHead icon="zap" tone="g-pink" title="Copilot" badge={<AIBadge />}>
        <button type="button" className="btn sm" onClick={() => setChat([])}>Clear</button>
      </CardHead>

      <div className="ai">
        <div className="msgs" ref={messagesRef}>
          {chat.length ? chat.map((m, i) => (
            <div className={`msg${m.role === 'user' ? ' me' : ''}`} key={i}>
              {m.role === 'user'
                ? <Avatar name={S.userName} />
                : <div className="who g-pink"><Icon name="zap" /></div>}
              <div className="bub">
                {m.role === 'user' ? m.content : <Markdown text={m.content} />}
              </div>
            </div>
          )) : (
            <div className="quiet">
              <div className="ic g-pink" style={{ width: 52, height: 52, borderRadius: 16, margin: '0 auto 12px' }}>
                <Icon name="zap" />
              </div>
              <b style={{ display: 'block', fontFamily: 'var(--h)', fontSize: 16, color: 'var(--ink)', marginBottom: 4 }}>
                Ask about your book
              </b>
              It can see every client, trial, payment and renewal in this workspace.
            </div>
          )}

          {chatBusy ? (
            <div className="msg">
              <div className="who g-pink"><Icon name="zap" /></div>
              <div className="bub"><Spinner /> Thinking…</div>
            </div>
          ) : null}
        </div>

        {chat.length ? null : (
          <div className="chips2">
            {COPILOT_PROMPTS.map(p => (
              <button type="button" className="chip2" key={p} onClick={() => submit(p)}>{p}</button>
            ))}
          </div>
        )}

        <div className="composer">
          <textarea
            ref={inputRef}
            placeholder="Ask anything about these clients…"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
            }}
          />
          <button type="button" className="btn primary" onClick={() => submit()} disabled={chatBusy}>
            <Icon name="plus" />Send
          </button>
        </div>
      </div>
    </Card>
  );
}
